import { useCallback, useMemo, useState, useRef } from "react";
import {
  HydratedCanvas,
  useCanvasConnectedAssets,
  useChangeCanvasOwner,
  useQuerySearchMembers,
} from "@zapier/client";
import { ChangeOwnerModal, ChangeOwnerUser } from "@zapier/shared";
import { AssetTypes, useNotification } from "@zapier/utils";
import { useEmitCanvasChangeOwnerModalEvent } from "@zapier/shared/src/events";
import { useTranslations } from "@zapier/i18n";

interface Props {
  canvas?: HydratedCanvas;
  isOpen: boolean;
  onCloseModal: () => void;
  folderId?: string;
}

const CanvasChangeOwnerModal = ({
  canvas,
  isOpen,
  onCloseModal,
  folderId,
}: Props) => {
  const { createNotification } = useNotification();
  const { emit } = useEmitCanvasChangeOwnerModalEvent();
  const t = useTranslations("fallbacks");

  const {
    transformedAssets,
    isLoading: isLoadingAssets,
    isFetching: isFetchingAssets,
  } = useCanvasConnectedAssets(isOpen ? canvas?.id : undefined);

  const [userSearch, setUserSearch] = useState("");
  const searchTimeoutReference = useRef<NodeJS.Timeout>();

  const { members, isLoading: isUsersLoading } = useQuerySearchMembers({
    search: userSearch,
    allowTeams: true,
  });

  const users: ChangeOwnerUser[] = useMemo(() => {
    return members.map((member) => {
      return {
        id: member.id,
        name: member.fullName,
        email: member.email,
        photoUrl: member.photoUrl,
      };
    });
  }, [members]);

  const { changeCanvasOwner } = useChangeCanvasOwner({
    folderId,
  });

  const onSearch = useCallback((search: string) => {
    // Debounce the search to avoid excessive API calls
    if (searchTimeoutReference.current) {
      clearTimeout(searchTimeoutReference.current);
    }
    searchTimeoutReference.current = setTimeout(() => {
      setUserSearch(search);
    }, 300);
  }, []);

  if (!canvas || !isOpen) {
    return;
  }

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      emit({ optionSelected: "cancel", canvasId: canvas.id });
      onCloseModal();
    }
  };

  const onSubmit = async (ownerId: string) => {
    if (ownerId) {
      emit({ optionSelected: "confirm", canvasId: canvas.id });
      await changeCanvasOwner(
        {
          canvasId: canvas.id || "",
          ownerId: ownerId,
        },
        {
          onSuccess: () => {
            createNotification({
              message: "Canvas owner changed successfully",
              type: "success",
            });
            emit({
              eventAction: "view",
              optionSelected: "success",
              canvasId: canvas.id,
            });
          },

          onError: (error) => {
            createNotification({
              message: `Failed to change canvas owner: ${error.message}`,
              type: "error",
            });
            emit({
              eventAction: "view",
              optionSelected: "error",
              canvasId: canvas.id,
            });
            setUserSearch("");
          },
        },
      );
    }
  };

  const canvasTitle =
    canvas.name || t("untitled-asset", { assetType: "Canvas" });

  return (
    <ChangeOwnerModal
      users={users}
      id={canvas.id ?? ""}
      title={canvasTitle}
      currentOwnerId={canvas.owner?.id}
      isOpen={isOpen}
      setIsOpen={handleOpenChange}
      linkedAssets={transformedAssets}
      assetType={AssetTypes.Canvas}
      isLoading={isLoadingAssets || isFetchingAssets}
      onSearch={onSearch}
      isUsersLoading={isUsersLoading}
      onSubmit={onSubmit}
    />
  );
};

export default CanvasChangeOwnerModal;
